import { BREAKPOINTS, COLORS, ComponentL, ComponentM, ComponentTextStyle, ComponentXL, ComponentXS, HyperLink, LoadingIndicator, ModalDialog, Size } from "@laerdal/life-react-components";
import React from "react";
import { useEffect, useState, useMemo } from "react";
import ProductApi from "../../services/api/ProductApi";
import { SanityDocumentDto } from "../../model/dto/sanityDocumentDto";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import posthog from 'posthog-js';
import { useMediaMatch } from "rooks";
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker to use local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface DownloadsAccordionChildProps {
  isSelected: boolean;
  code: string;
  name: string;
}

const DownloadItemContainer = styled.div`
    padding: 20px 0;
`
const DownloadFirstLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`

const DownloadButton = styled.div`
    display: flex;
    padding: 0 16x 0 32px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`

const AttachmentContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  background: ${COLORS.white};
  overflow-y: auto;
  height: 500px;

  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .react-pdf__Page {
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .react-pdf__Page__canvas {
    max-width: 100%;
    height: auto !important;
  }
`;

const DownloadContainer = styled.div`
    border-top: 1px solid ${COLORS.neutral_200};
    border-bottom: 1px solid ${COLORS.neutral_200};

    padding: 12px 24px;
`

const USERGUIDES_GROUP = "user guide";
const HTML_DOCUMENT_TYPE = "html document";
const PDF_DOCUMENT_TYPE = "pdf document";

const DownloadsAccordionChild: React.FC<DownloadsAccordionChildProps> = ({ isSelected, code, name }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [documents, setDocuments] = useState<SanityDocumentDto[]>([]);
  const { t } = useTranslation("landingPage");

  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
  const isMobile = !isMediumScreen;

  const [attachmentPreviewModalOpen, setAttachmentPreviewModalOpen] = useState<boolean>(false);
  //const [attachmentPreviewModalLoading, setAttachmentPreviewModalLoading] = useState<boolean>(false);
  const [attachmentPreviewLink, setAttachmentPreviewLink] = useState<string>('');
  const [attachmentPreviewFileName, setAttachmentPreviewFileName] = useState<string>('');
  const [numPages, setNumPages] = useState<number | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const groupDocumentsByType = (docs: SanityDocumentDto[]): Record<string, SanityDocumentDto[]> => {
    return docs.reduce<Record<string, SanityDocumentDto[]>>((acc, doc) => {
      const key = (doc.documentType?.name?.trim() || "Other");
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    }, {});
  };

  const sortGroups = (groups: Record<string, SanityDocumentDto[]>): [string, SanityDocumentDto[]][] => {
    return Object.entries(groups).sort(([typeA], [typeB]) => {
      const lowerA = typeA.toLowerCase();
      const lowerB = typeB.toLowerCase();
      if (lowerA === USERGUIDES_GROUP) return -1;
      if (lowerB === USERGUIDES_GROUP) return 1;
      return typeA.localeCompare(typeB);
    });
  };

  const sortDocuments = (docs: SanityDocumentDto[]): SanityDocumentDto[] => {
    return [...docs].sort((a, b) => {
      const typeA = (a.typeName || "").toLowerCase();
      const typeB = (b.typeName || "").toLowerCase();
      if (typeA === HTML_DOCUMENT_TYPE && typeB !== HTML_DOCUMENT_TYPE) return -1;
      if (typeA !== HTML_DOCUMENT_TYPE && typeB === HTML_DOCUMENT_TYPE) return 1;
      return (a.displayName || "").localeCompare(b.displayName || "");
    });
  };

  const groupedAndSortedDocuments = useMemo(() => {
    if (!documents || documents.length === 0) return [];

    const groups = groupDocumentsByType(documents);
    const sortedGroups = sortGroups(groups);

    return sortedGroups.map(([type, docs]) => ({
      type,
      sortedDocs: sortDocuments(docs)
    }));
  }, [documents]);

  useEffect(() => {
    if (!isSelected) return;
    let cancelled = false;
    setLoading(true);

    posthog?.capture('SUPP-DownloadsPage-AccordionExpanded', {
      component: 'DownloadsAccordionChild',
      productLineCode: code,
      productLineName: name
    });

    ProductApi.GetDocumentsByProductLineCode(code)
      .then((docs) => {
        if (cancelled) return;
        setDocuments(docs);
        posthog?.capture('SUPP-DownloadsPage-DocumentsLoaded', {
          component: 'DownloadsAccordionChild',
          productLineCode: code,
          productLineName: name,
          documentCount: docs.length
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSelected, code]);

  if (loading)
    return <DownloadContainer> <LoadingIndicator /> </DownloadContainer>;

  return (
    <DownloadContainer>
      <ModalDialog
        isModalOpen={attachmentPreviewModalOpen}
        closeAction={() => setAttachmentPreviewModalOpen(false)}
        submitAction={(e) => e?.preventDefault()}
        size={isMobile ? Size.Small : Size.Large}
        title={attachmentPreviewFileName}
        contentOverflow={'auto'}
        buttons={[
          {
            id: 'download',
            variant: 'tertiary',
            text: t('Download'),
            action: () => {
              console.log('Download clicked', attachmentPreviewLink);
              posthog?.capture('SUPP-DownloadsPage-DocumentDownloadFromPreview', {
                component: 'DownloadsAccordionChild',
                productLineCode: code,
                productLineName: name,
                documentName: attachmentPreviewFileName,
                documentLink: attachmentPreviewLink
              });
              window.open(attachmentPreviewLink, '_blank');
              setAttachmentPreviewModalOpen(false);
            },
            disabled: false, //attachmentPreviewModalLoading,
            type: 'button',
          },
          {
            id: 'close',
            variant: 'primary',
            type: 'button',
            text: t('Close'),
            action: () => {
              setAttachmentPreviewModalOpen(false);
            },
          },
        ]}>
        <AttachmentContainer>
          <Document
            file={attachmentPreviewLink}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<LoadingIndicator />}
            error={<div>Failed to load PDF. <a href={attachmentPreviewLink} target="_blank" rel="noopener noreferrer">Download instead</a></div>}
          >
            {numPages && Array.from(new Array(numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={isMobile ? 300 : 800}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </Document>
        </AttachmentContainer>
      </ModalDialog>

      {groupedAndSortedDocuments.length === 0 && (
        <ComponentM>{t('No documents available')}</ComponentM>
      )}
      {groupedAndSortedDocuments.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {groupedAndSortedDocuments.map(({ type, sortedDocs }) => (
            <div key={type}>
              <ComponentXL textStyle={ComponentTextStyle.Bold}>
                {type}
              </ComponentXL>


              {sortedDocs.map((doc, idx) => (
                <>
                  {(doc.typeName || "").toLowerCase() == HTML_DOCUMENT_TYPE &&
                    <DownloadItemContainer>
                      <DownloadFirstLine key={doc.contentLink + idx}>
                        <ComponentL>{doc.displayName}</ComponentL>
                        <DownloadButton>
                          <HyperLink
                            href={doc.contentLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant={"default"}
                            onClick={() => {
                              posthog?.capture('SUPP-DownloadsPage-DocumentView', {
                                component: 'DownloadsAccordionChild',
                                productLineCode: code,
                                productLineName: name,
                                documentType: type,
                                documentName: doc.displayName,
                                documentLink: doc.contentLink,
                                documentFormat: 'HTML'
                              });
                            }}>
                            {t('View')}
                          </HyperLink>
                        </DownloadButton>
                      </DownloadFirstLine>
                      <ComponentXS>{doc.languages?.map(language => language.name).join(', ') || ''}</ComponentXS>
                      {/* <ComponentS>{doc.products?.map(product => product.title).join(', ') || ''}</ComponentS> */}
                      <ComponentXS>{doc.productLines?.map(productLine => productLine).join(', ') || ''}</ComponentXS>
                    </DownloadItemContainer>}

                  {(doc.typeName || "").toLowerCase() == PDF_DOCUMENT_TYPE &&
                    <DownloadItemContainer>
                      <DownloadFirstLine key={doc.contentLink + idx}>
                        <ComponentL>{doc.displayName}</ComponentL>
                        <DownloadButton>
                          <HyperLink
                            onClick={(e) => {
                              e.preventDefault();
                              posthog?.capture('SUPP-DownloadsPage-DocumentPreview', {
                                component: 'DownloadsAccordionChild',
                                productLineCode: code,
                                productLineName: name,
                                documentType: type,
                                documentName: doc.displayName,
                                documentLink: doc.contentLink,
                                documentFormat: 'PDF'
                              });
                              setAttachmentPreviewModalOpen(true);
                              setAttachmentPreviewLink(doc.contentLink);
                              setAttachmentPreviewFileName(doc.displayName);

                              console.log('Preview')
                            }}
                            href={doc.contentLink}
                            target="_blank"
                            rel="noopener noreferrer" variant={"default"}>
                            {t('Preview')}
                          </HyperLink>
                        </DownloadButton>
                      </DownloadFirstLine>
                      <ComponentXS>{doc.languages?.map(language => language.name).join(', ') || ''}</ComponentXS>
                      {/* <ComponentS>{doc.products?.map(product => product.title).join(', ') || ''}</ComponentS> */}
                      <ComponentXS>{doc.productLines?.map(productLine => productLine).join(', ') || ''}</ComponentXS>
                    </DownloadItemContainer>}

                  {(doc.typeName || "").toLowerCase() != HTML_DOCUMENT_TYPE &&
                    (doc.typeName || "").toLowerCase() != PDF_DOCUMENT_TYPE &&
                    <DownloadItemContainer>
                      <DownloadFirstLine key={doc.contentLink + idx}>
                        <ComponentL>{doc.displayName}</ComponentL>
                        <DownloadButton>
                          <HyperLink
                            href={doc.contentLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant={"default"}
                            onClick={() => {
                              posthog?.capture('SUPP-DownloadsPage-DocumentDownload', {
                                component: 'DownloadsAccordionChild',
                                productLineCode: code,
                                productLineName: name,
                                documentType: type,
                                documentName: doc.displayName,
                                documentLink: doc.contentLink,
                                documentFormat: doc.typeName || 'Unknown'
                              });
                            }}>
                            {t('Download')}
                          </HyperLink>
                        </DownloadButton>
                      </DownloadFirstLine>
                      <ComponentXS>{doc.languages?.map(language => language.name).join(', ') || ''}</ComponentXS>
                      <ComponentXS>{doc.productLines?.map(productLine => productLine).join(', ') || ''}</ComponentXS>
                    </DownloadItemContainer>}

                </>
              ))}
            </div>
          ))}
        </div>
      )}

    </DownloadContainer>
  );
};

export default DownloadsAccordionChild;
